import store from '@/store/index'
import router from '@/router/index'
import { getLocalizedText } from '@/utils/i18n'
import { ref } from 'vue'
import userApi from '@/api/user'

export const WASH_REQUEST_FIELD_CHANGE_STEP = Object.freeze({
  EDIT: 'edit',
  CONFIRM: 'confirm',
  COMPLETE: 'complete',
})

function isValidationErrors(errors) {
  return !!errors && typeof errors === 'object' && !Array.isArray(errors) && Object.keys(errors).length > 0
}

export function useWashRequestFieldChange({
  getWashRequestId,
  convertWashRequest,
  applyFetchedWashRequest,
  callUpdateApi,
  isConfirmDisabled,
  isSubmitBlocked = () => false,
  onSubmitSuccess = () => {},
  clearFieldErrors = () => {},
  redirectRouteName = 'wash-request-currents',
}) {
  const isSubmitting = ref(false)
  const currentStep = ref(WASH_REQUEST_FIELD_CHANGE_STEP.EDIT)
  const currentWashRequest = ref(null)
  const showWithdrawnError = ref(false)
  const serverErrors = ref([])

  function scrollTop() {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 0)
  }

  function redirectToCurrents() {
    router.push({ name: redirectRouteName })
  }

  function clearErrors() {
    showWithdrawnError.value = false
    serverErrors.value = []
    clearFieldErrors()
  }

  async function fetchCurrentWashRequest() {
    try {
      const { data } = await userApi.getWashRequest(getWashRequestId())
      if (!data) {
        redirectToCurrents()
        return
      }

      const converted = convertWashRequest(data)
      currentWashRequest.value = converted
      applyFetchedWashRequest(converted)
    } catch (error) {
      redirectToCurrents()
    }
  }

  function goToConfirm() {
    if (isConfirmDisabled()) {
      return
    }
    clearErrors()
    currentStep.value = WASH_REQUEST_FIELD_CHANGE_STEP.CONFIRM
    scrollTop()
  }

  function backToEdit() {
    currentStep.value = WASH_REQUEST_FIELD_CHANGE_STEP.EDIT
    scrollTop()
  }

  function handleUpdateError(err) {
    const responseData = err?.response?.data

    if (responseData?.reason === 'status_withdrawn') {
      showWithdrawnError.value = true
      store.dispatch('localStorage/set', {
        key: 'serverToken',
        val: null,
      })
      return
    }

    if (isValidationErrors(responseData?.errors)) {
      serverErrors.value = Object.keys(responseData.errors).reduce(
        (messages, field) => messages.concat(responseData.errors[field]), [])
      return
    }

    serverErrors.value = [getLocalizedText('washRequest.errors.updateNotAllowed')]
  }

  async function submit() {
    if (isSubmitting.value || isSubmitBlocked()) {
      return
    }

    clearErrors()
    isSubmitting.value = true
    try {
      await callUpdateApi()
      await onSubmitSuccess()
      currentStep.value = WASH_REQUEST_FIELD_CHANGE_STEP.COMPLETE
      scrollTop()
    } catch (err) {
      handleUpdateError(err)
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    isSubmitting,
    currentStep,
    currentWashRequest,
    showWithdrawnError,
    serverErrors,
    fetchCurrentWashRequest,
    goToConfirm,
    backToEdit,
    submit,
    clearErrors,
    redirectToCurrents,
  }
}
